import type { ReactNode } from 'react'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface ExtensionSettingsShellProps {
  title: string
  description?: string
  enabled: boolean
  onEnabledChange?: (enabled: boolean) => void
  canToggle?: boolean
  toggling?: boolean
  statusLabel?: string
  children: ReactNode
  className?: string
  'data-testid'?: string
}

/**
 * Standard Settings chrome for a workspace extension page.
 * Enable toggle + status + body slot (connect, instructions, limb panels).
 */
export function ExtensionSettingsShell({
  title,
  description,
  enabled,
  onEnabledChange,
  canToggle = true,
  toggling = false,
  statusLabel,
  children,
  className,
  'data-testid': dataTestId,
}: ExtensionSettingsShellProps) {
  return (
    <div
      className={cn('space-y-6', className)}
      data-testid={dataTestId}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
            {statusLabel ? (
              <Badge
                variant="outline"
                className={cn(
                  'text-xs font-medium shrink-0',
                  /^(on|enabled|connected)$/i.test(statusLabel)
                    ? 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400'
                    : 'text-muted-foreground',
                )}
              >
                {statusLabel}
              </Badge>
            ) : null}
          </div>
          {description ? (
            <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {onEnabledChange ? (
          <div className="flex items-center gap-2 shrink-0 pt-1">
            <span className="text-xs text-muted-foreground">
              {enabled ? 'On' : 'Off'}
            </span>
            <Switch
              checked={enabled}
              disabled={!canToggle || toggling}
              onCheckedChange={onEnabledChange}
              aria-label={`Enable ${title}`}
            />
          </div>
        ) : null}
      </div>
      <div className="space-y-6 border-t border-border pt-6">{children}</div>
    </div>
  )
}
